import {Box, Stack, Tooltip, Typography} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {Transaction} from "../../../../../sdo/Transaction.ts";
import TransactionView from "./TransactionView.tsx";

interface TransactionCardProps {
    transaction: Transaction;
    onNavigate: (transaction: Transaction) => void;
}

function TransactionCard({transaction, onNavigate}: TransactionCardProps) {
    const handleNavigate = () => {
        onNavigate(transaction);
    };

    const typeLabel = transaction.typeKey && transaction.typeKey.trim().length > 0
        ? transaction.typeKey
        : "Transaction";

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            width: "100%",
            minWidth: "500px",
            background: "linear-gradient(155deg, rgba(0, 71, 106, 0.45) 0%, rgba(0, 38, 53, 0.45) 25%, rgba(0, 0, 0, 0.45) 90%, rgba(0, 0, 0, 0.55) 100%)",
            borderRadius: "15px",
            border: "0.5px solid #3F464F",
            overflow: "hidden",
            transition: "border-color 0.2s",
        }}>
            {/* Center: header + content */}
            <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column", background: "linear-gradient(180deg, rgba(22, 30, 38, 0.9) 0%, rgba(14, 18, 24, 0.95) 100%)", borderRight: "1px solid rgba(63, 70, 79, 0.3)"}}>
                {/* Header row */}
                <Box sx={{borderBottom: "1px solid rgba(63, 70, 79, 0.5)", padding: "8px 12px"}}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1.5} sx={{
                        width: "100%",
                        maxWidth: 760,
                        mx: "auto",
                    }}>
                        <img src="/images/transactions.svg" width={24} height={24} alt="Transaction" style={{opacity: 0.9}}/>
                        <Typography sx={{
                            color: "#f2f5fa",
                            fontWeight: 700,
                            fontSize: "1.00rem",
                            whiteSpace: "nowrap",
                        }}>
                            {typeLabel}
                        </Typography>
                        <Box sx={{
                            width: "1px",
                            height: "20px",
                            backgroundColor: "rgba(112, 147, 160, 0.4)",
                            marginLeft: "4px",
                        }}/>
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.45)",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                        }}>
                            Display:
                        </Typography>
                        <Typography sx={{
                            color: "#02cfee",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "420px",
                        }}>
                            {transaction.display || "—"}
                        </Typography>
                    </Stack>
                </Box>

                {/* Content area (view-only) */}
                <Box sx={{padding: "8px 12px", minHeight: "40px"}}>
                    <TransactionView transaction={transaction}/>
                </Box>
            </Box>

            {/* Right action panel — navigate */}
            <Tooltip title="View Transaction" arrow placement="right" slotProps={{tooltip: {sx: {background: "linear-gradient(to right, rgba(41, 53, 64, 0.775), rgba(13, 14, 15, 0.775))", border: "1.5px solid #293540", borderRadius: "10px"}}, arrow: {sx: {color: "#293540"}}}}>
                <Box
                    onClick={handleNavigate}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "10px",
                        opacity: 0.7,
                        cursor: "pointer",
                        transition: "opacity 0.15s",
                        "&:hover": {opacity: 1},
                    }}
                >
                    <ChevronRightIcon sx={{fontSize: 28, color: "#f2f5fa"}}/>
                </Box>
            </Tooltip>
        </Box>
    );
}

export default TransactionCard;
